import { EventType, ValidationAssetStatus } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { RealtimeConnectionState, subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { type ValidationAssetRequest, workOrderApi } from '@/services/work-order.service'
import { workOrderFilesKey } from './useWorkOrderFiles'

/** Query key for a work order's validation-asset requests. */
export const WORK_ORDER_VALIDATION_ASSETS_KEY = 'work-order-validation-assets'

export const workOrderValidationAssetsKey = (workOrderId: string) => [WORK_ORDER_VALIDATION_ASSETS_KEY, workOrderId]

/** Statuses a request no longer moves out of — polling can stop once all are here. */
const TERMINAL_STATUSES: ReadonlySet<ValidationAssetStatus> = new Set([
	ValidationAssetStatus.READY,
	ValidationAssetStatus.FAILED,
])

/** Poll cadence while any request is still `requested`/`rendering`. */
const REFETCH_INTERVAL_MS = 5000

/**
 * A work order's validation-asset requests (newest first). A request is rendered
 * asynchronously by the pdf-generator-worker, so the panel has to wait for it to
 * reach `ready`/`failed`. Two ways of waiting, chosen by connection state
 * (ADR-0020):
 *
 * - **Connected** — a subscription to the render events of the requests actually
 *   on screen. No interval at all, so a slow render costs nothing while it runs.
 * - **Otherwise** — poll every 5s while any request is non-terminal, stopping
 *   once every one is `ready`/`failed`.
 *
 * The switch is automatic: losing the connection mid-render resumes polling.
 */
export function useWorkOrderValidationAssets(workOrderId: string | null) {
	const queryClient = useQueryClient()
	const isPushing = useRealtimeConnectionState() === RealtimeConnectionState.CONNECTED

	const query = useQuery<ValidationAssetRequest[]>({
		queryKey: workOrderValidationAssetsKey(workOrderId ?? ''),
		queryFn: () => workOrderApi.listValidationAssetRequests(workOrderId as string),
		enabled: !!workOrderId,
		refetchInterval: (q) => {
			if (isPushing) return false // the subscription does the waiting
			const requests = q.state.data ?? []
			const anyPending = requests.some((r) => !TERMINAL_STATUSES.has(r.status))
			return anyPending ? REFETCH_INTERVAL_MS : false
		},
	})

	// A string, not an array: the query returns a fresh array on every refetch, so
	// an array dependency would re-register the subscription each time. Joining the
	// ids makes an unchanged set compare equal.
	const pendingIdKey = (query.data ?? [])
		.filter((r) => !TERMINAL_STATUSES.has(r.status))
		.map((r) => r.requestId)
		.join(',')

	useEffect(() => {
		const pendingRequestIds = pendingIdKey ? pendingIdKey.split(',') : []
		if (!isPushing || !workOrderId || pendingRequestIds.length === 0) return

		// Invalidate and let the query re-read; the payload is deliberately not
		// inspected. Whether the render produced a usable manifest is the refetched
		// request row's answer, and a failure ends the wait the same way a success
		// does — the user-facing error comes from the notification bridge.
		return subscribeRealtime(
			{
				type: [EventType.VALIDATION_ASSET_READY, EventType.VALIDATION_ASSET_FAILED],
				entityRef: { id: pendingRequestIds },
			},
			() => {
				void queryClient.invalidateQueries({ queryKey: workOrderValidationAssetsKey(workOrderId) })
			},
		)
	}, [isPushing, pendingIdKey, workOrderId, queryClient])

	return query
}

/**
 * Delete a validation-asset request (soft-deletes the request row AND its produced
 * files). On success refreshes the panel's request list and the order's file list
 * (`orderCode`-keyed), since the produced files are gone from classification search.
 */
export const useDeleteValidationAssetRequest = (workOrderId: string, orderCode: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (requestId: string) => workOrderApi.deleteValidationAssetRequest(workOrderId, requestId),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: workOrderValidationAssetsKey(workOrderId) })
			void qc.invalidateQueries({ queryKey: workOrderFilesKey(orderCode) })
		},
	})
}
